/**
 * Build a hosted agent from a business's website. OUR TEAM'S SCREEN: the link to it is drawn
 * for an internal reader only, and the Worker answers 404 to anybody else.
 *
 * ONE PRESS CREATES THE AGENT. The organization too, when it is new. The agent exists at once,
 * hosted and at every default, and a background run fills it in from the site while the next
 * screen (`AgentBuild`) shows how far it has got. What it writes is reviewed on Settings, like
 * any agent: there is no draft to approve here.
 */

import { useState } from "react";
import type { FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { ApiError, post, useApi, type BuildProgress, type OrganizationChoice } from "../api";
import { PageHead } from "../components/Page";
import { Field } from "../components/Form";
import { Button, Select, SettingCard, TextArea, TextInput } from "../ui";
import { useCurrentAgent } from "../currentAgent";
import { useSession } from "../session";
import { Empty } from "../components/States";

const MAX_NOTES = 4_000;
const NEW = "__new__";

export function NewAgent() {
  const { me } = useSession();
  const { reload } = useCurrentAgent();
  const navigate = useNavigate();
  const organizations = useApi<{ organizations: OrganizationChoice[] }>(
    me?.user.role === "internal" ? "/v1/build/organizations" : null,
  );

  const [website, setWebsite] = useState("");
  const [notes, setNotes] = useState("");
  const [organization, setOrganization] = useState(NEW);
  const [organizationName, setOrganizationName] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [notifyEmail, setNotifyEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (me?.user.role !== "internal") {
    return <Empty title="No such page" body="That address does not match anything in this dashboard." action={{ label: "Go to the overview", to: "/" }} />;
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const created = await post<{ agent: { id: string; slug: string }; build: BuildProgress }>("/v1/build", {
        websiteUrl: website,
        notes,
        businessName,
        notifyEmail,
        ...(organization === NEW ? { organizationName } : { organizationId: organization }),
      });
      // The list learns about the new agent before its address is opened, so the switcher has
      // it and the address resolves.
      await reload();
      navigate(`/${created.agent.slug}/build`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "The agent could not be created. Try again.");
      setBusy(false);
    }
  }

  const choices = organizations.data?.organizations ?? [];

  return (
    <>
      <PageHead
        title="Build an agent from a website"
        lede="We read their site, save its pages as the agent's knowledge, and write its instructions, greeting and actions. You review it all on Settings."
      />
      <form onSubmit={submit} className="mx-auto grid w-full max-w-[720px] gap-5">
        <SettingCard className="grid gap-5 p-6">
          <Field label="Their website" hint="Their own site. Up to 15 of its pages are read.">
            <TextInput
              required
              autoFocus
              inputMode="url"
              placeholder="northsidedriving.com.au"
              value={website}
              onChange={(event) => setWebsite(event.target.value)}
              className="w-full"
            />
          </Field>

          <Field
            label="What we know"
            hint="Anything the site doesn't say: what they want the agent to do, what they don't offer. Your agent can repeat this to callers, so write only what's true about the business."
            count={`${notes.length.toLocaleString()} / ${MAX_NOTES.toLocaleString()}`}
          >
            <TextArea
              rows={6}
              maxLength={MAX_NOTES}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              className="w-full"
            />
          </Field>
        </SettingCard>

        <SettingCard className="grid gap-5 p-6">
          <Field label="Organization" hint="Who pays for it. A new one has nobody in it until you invite them from Team.">
            <Select value={organization} onChange={(event) => setOrganization(event.target.value)} className="w-full">
              <option value={NEW}>New organization…</option>
              {choices.map((choice) => (
                <option key={choice.id} value={choice.id}>
                  {choice.name} ({choice.agents} {choice.agents === 1 ? "agent" : "agents"})
                </option>
              ))}
            </Select>
          </Field>
          {organization === NEW && (
            <Field label="New organization's name">
              <TextInput
                required
                maxLength={80}
                value={organizationName}
                onChange={(event) => setOrganizationName(event.target.value)}
                className="w-full"
              />
            </Field>
          )}
          <Field label="Business name" hint="Optional. Leave it empty to use the name their site gives.">
            <TextInput maxLength={80} value={businessName} onChange={(event) => setBusinessName(event.target.value)} className="w-full" />
          </Field>
          <Field label="Send messages to" hint="Optional. Where the agent's actions email what callers leave.">
            <TextInput type="email" value={notifyEmail} onChange={(event) => setNotifyEmail(event.target.value)} className="w-full" />
          </Field>
        </SettingCard>

        {error && (
          <p role="alert" className="text-sm text-alert">
            {error}
          </p>
        )}
        <div className="flex justify-end">
          <Button type="submit" variant="primary" disabled={busy}>
            {busy ? "Creating…" : "Build the agent"}
          </Button>
        </div>
      </form>
    </>
  );
}
